import React, { useState, useEffect } from "react";
import { Form, Button } from "semantic-ui-react";
import { AcademicCycle, Grade, Student, Report } from "../../../../../api";
import { useAuth } from "../../../../../hooks";
import "./NotesByStudent.scss";

const academicCycleController = new AcademicCycle();
const gradeController = new Grade();
const studentController = new Student();
const reportController = new Report();

export function NotesByStudent(props) {
  const { onClose } = props;
  const { accessToken } = useAuth();
  const [academicCycles, setAcademicCycles] = useState([]);
  const [selectedAcademicCycle, setSelectedAcademicCycle] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [loadingExcel, setLoadingExcel] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await academicCycleController.getAcademicCycles();
        const academicCyclesOptions = response.map((academicCycle) => ({
          key: academicCycle.id,
          value: academicCycle.id,
          levelId: academicCycle.levelId,
          text: `${academicCycle.Level.name} - ${academicCycle.name}`,
        }));
        setAcademicCycles(academicCyclesOptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [accessToken]);

  useEffect(() => {
    if (!selectedLevel) return;
    (async () => {
      try {
        const response = await gradeController.getGradesByLevelId(selectedLevel, accessToken);
        const gradesOptions = response.map((grade) => ({
          key: grade.id,
          value: grade.id,
          text: `${grade.name}`,
        }));
        setGrades(gradesOptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [selectedLevel, accessToken]);

  useEffect(() => {
    if (!selectedGrade || !selectedAcademicCycle) {
      setStudents([]); // Limpiar estudiantes si falta algún filtro
      setSelectedStudent(null); // Limpiar estudiante seleccionado
      return;
    }

    (async () => {
      try {
        const response = await studentController.getStudentsByGradeAndAcademicCycle(accessToken, selectedGrade, selectedAcademicCycle);
        const studentsOptions = response.map((student) => ({
          key: student.id,
          value: student.id,
          text: `${student.User?.lastname} ${student.User?.secondLastname} ${student.User?.firstname}`,
        }));
        setStudents(studentsOptions);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [selectedGrade, selectedAcademicCycle, accessToken]);

  // Manejar cambio de ciclo académico
  const handleAcademicCycleChange = (_, { value }) => {
    setSelectedAcademicCycle(value);
    
    // Encontrar el ciclo seleccionado y obtener su levelId
    const selectedCycle = academicCycles.find(cycle => cycle.value === value);
    if (selectedCycle) {
      setSelectedLevel(selectedCycle.levelId); // Actualizar el nivel
      setSelectedGrade(null); // Limpiar el grado seleccionado
      setGrades([]); // Limpiar los grados disponibles
    }
  };

  const handleGradeChange = (_, { value }) => {
    setSelectedGrade(value);
    setSelectedStudent(null); // Limpiar estudiante seleccionado
    setStudents([]); // Limpiar lista de estudiantes
  };

  const handleExportExcel = async () => {
    setLoadingExcel(true);
    try {
      const response = await reportController.notesByStudent(
        accessToken,
        selectedAcademicCycle,
        selectedGrade,
        selectedStudent,
        "excel"
      );

      // Crear un Blob (Binary Large Object) desde la respuesta
      // Un Blob es un objeto que representa datos binarios (como un archivo)
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob); // URL temporal en el navegador
      const a = document.createElement("a");
      a.href = url;
      a.download = `notas_estudiante_${selectedAcademicCycle}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // onClose(); // Cerrar el modal después de exportar
      
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    setLoadingPDF(true);
    try {
      const response = await reportController.notesByStudent(
        accessToken,
        selectedAcademicCycle,
        selectedGrade,
        selectedStudent,
        "pdf"
      );

      const url = window.URL.createObjectURL(response);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asistencia_grado_${selectedAcademicCycle}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      // onClose(); // Cerrar el modal después de exportar
      
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPDF(false);
    }
  };

  // Deshabilitar ambos botones si alguno está cargando
  const isDisabled = !selectedAcademicCycle || !selectedGrade || !selectedStudent || loadingExcel || loadingPDF;

  return (
    <div className="notes-by-grade-modal">
      <Form>
        <Form.Group widths="equal">
          <Form.Select
            className="edunova-select"
            label="Año académico"
            placeholder="Selecciona un año académico"
            fluid
            clearable
            options={academicCycles}
            value={selectedAcademicCycle}
            onChange={handleAcademicCycleChange}
            disabled={loadingExcel || loadingPDF}
          />
          <Form.Select
            className="edunova-select"
            label="Grado"
            placeholder="Selecciona un grado"
            fluid
            clearable
            options={grades}
            value={selectedGrade}
            onChange={handleGradeChange}
            disabled={!selectedAcademicCycle || loadingExcel || loadingPDF}
          />
        </Form.Group>

        <Form.Select
          className="edunova-select"
          label="Estudiante"
          placeholder="Selecciona un estudiante"
          fluid
          clearable
          options={students}
          value={selectedStudent}
          onChange={(_, { value }) => setSelectedStudent(value)}
          disabled={loadingExcel || loadingPDF}
        />
      </Form>

      <div className="notes-by-grade-modal__actions">
        <Button
          color="green"
          icon="file excel"
          content="Exportar a Excel"
          onClick={handleExportExcel}
          loading={loadingExcel}
          disabled={isDisabled}
        />
        <Button
          color="red"
          icon="file pdf"
          content="Exportar a PDF"
          onClick={handleExportPDF}
          loading={loadingPDF}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
}